// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { InstrumentTypes } from "../../Utils/Instruments/InstrumentType.js"
import { MarginTypes } from "../../Utils/Instruments/MarginTypes.js"
import { ProductType } from "../../Utils/Instruments/ProductType.js"
import { RiskPlanDataKey } from "../../Utils/RiskPlan/RiskPlanDataKey.js"

export let RiskPlan = function (dataCache)
{
    this.DataCache = dataCache;

    this.Id = 0
    this.Name = null
    this.riskPlanCache = new RiskPlanCache()
    // <key, RiskPlanSettings>
    this.items = {}
}

RiskPlan.prototype.update = function (dataCache, message)
{
    this.Id = message.Id
    if (message.Name)
        this.Name = message.Name

    let riskPlanCache = this.riskPlanCache
    let data = message.Data
    for (let i = 0, len = data.length; i < len; i++)
        riskPlanCache.addRiskData(data[i])

    riskPlanCache.UpdateCount();

    // TODO.
    let instruments = dataCache.getCachedInstrumentDict()
    let newItems = {}

    for (let key in instruments)
        this.processOneInstrument(instruments[key], newItems)

    this.items = newItems
}

RiskPlan.prototype.applyToInstrument = function ()
{
    for (let it in this.items)
    {
        let item = this.items[it];
        let instrument = this.DataCache.getInstrumentByName(it);
        if (!instrument)
            continue
        instrument.RiskSettings.RiskPlanSettings = item
        instrument.RiskSettingsUpdated.Raise();
    }
}

RiskPlan.prototype.processOneInstrument = function (instrument, newItems)
{
    newItems = newItems || this.items
    let item = this.riskPlanCache.getRiskPlanSettings(instrument)
    newItems[instrument.GetInteriorID()] = item

    /* TODO.
    var dc = instrument.DataCache;
    Account myAccount = (dc != null && dc.LoginUser != null) ? dc.LoginUser.Account : null;
    if (myAccount != null && myAccount.RiskPlan == this)
        instrument.RiskSettings.RiskPlanSettings = item;
    */

    // TODO. Use current acc risk plan.
    instrument.RiskSettings.RiskPlanSettings = item
    instrument.RiskSettingsUpdated.Raise();
}

RiskPlan.prototype.GetRisksForInstrument = function (instrumentName)
{
    let item = this.items[instrumentName];

    if (!item)
        item = new RiskPlanSettings();

    return item;
}

//margin
RiskPlan.GetRiskPlanItem = function (instrument, productType)
{
    let rs = instrument.RiskSettings;
    let rps = rs.RiskPlanSettings;
    let cache = rps.cache;
    let ri = cache[productType];

    return ri;
}
//GetMargin
RiskPlan.GetMargin = function (instrument, productType, tier)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return { Warning: 0, Initial: 0, Maintenance: 0 };

    if (RiskPlan.IsPriceBasedMultipleCoefMarginType(instrument, productType))
        return { Leverage: ri.GetLeverageTier(tier) }   //#113782 comments

    return { Warning: ri.GetWarnMargin(tier), Initial: ri.GetInitMargin(tier), Maintenance: ri.GetMaintMargin(tier) };
}

RiskPlan.GetMarginCoeficientListLength = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri || !ri.MarginCoeficientList)
        return 0;

    if (!RiskPlan.IsTierMargin(instrument, productType))    // #95613   подозреваю баг на сервере из-за которого присылаются лишние коэф.
        return 1                                            // несколько слоев коэф. может быть только у TierMargin, у остальных 1

    let list = ri.MarginCoeficientList

    return list.length ? list.length : 0
}

RiskPlan.IsTierMargin = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return false;

    const marginType = ri.GetMarginType();
    return marginType === MarginTypes.TIERED_PRICE_BASED_CUSTOM_COEF || marginType === MarginTypes.TIERED_PRICE_BASED_CUSTOM_COEF_WITH_RANGES
}

RiskPlan.GetOvernightMargin = function (instrument, productType, tier)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return { Warning: 0, Initial: 0, Maintenance: 0 };

    return { Warning: ri.GetWarnMarginOvernight(tier), Initial: ri.GetInitMarginOvernight(tier), Maintenance: ri.GetMaintMarginOvernight(tier) };
}

RiskPlan.GetShortMargin = function (instrument, productType, tier)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return { Warning: 0, Initial: 0, Maintenance: 0 };

    return { Warning: ri.GetWarnMarginShort(tier), Initial: ri.GetInitMarginShort(tier), Maintenance: ri.GetMaintMarginShort(tier) };
}

RiskPlan.GetOvernightShortMargin = function (instrument, productType, tier)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return { Warning: 0, Initial: 0, Maintenance: 0 };

    return { Warning: ri.GetWarnMarginOvernightShort(tier), Initial: ri.GetInitMarginOvernightShort(tier), Maintenance: ri.GetMaintMarginOvernightShort(tier) };
}
//UseMargin
RiskPlan.UseOvernightMargin = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return false;

    return ri.GetUseOvernightMargin();
}

RiskPlan.UseLongShortMargin = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return false;

    return ri.GetUseLongShortMargin();
}
//CoeffNullable
RiskPlan.GetUseVarOnlyIntraday = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return false;

    return ri.GetUseVarOnlyIntraday();

}

RiskPlan.GetVarCoeffNullable = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return null;

    return ri.GetVarCoeff();
}

RiskPlan.GetVarCoeffApplyingNullable = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return null;

    return ri.GetVarCoeffApplying();
}
//GetType
RiskPlan.GetCalculateMarginTypeNullable = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return null;

    return ri.GetMarginType();
}
//ProductTypes
RiskPlan.GetAvailableProductTypes = function (instrument)
{
    return instrument.RiskSettings.RiskPlanSettings.availableProductTypes;
}

RiskPlan.AvailableOnlyOneProductType = function (instrument)
{
    return instrument.RiskSettings.RiskPlanSettings.availableProductTypes.length == 1;
}

RiskPlan.IsProductTypeAvailableForInstrument = function (instrument, productType)
{
    let availableProductTypes = instrument.RiskSettings.RiskPlanSettings.availableProductTypes

    for (let i = 0; i < availableProductTypes.length; i++)
        if (availableProductTypes[i] == productType)
            return true

    return false
}

//Leverages #111414->#112880->#112886
RiskPlan.GetLeverages = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return [];

    return ri.GetLeverages();
}

RiskPlan.IsPriceBasedMultipleCoefMarginType = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return false;

    return ri.IsPriceBasedMultipleCoefMarginType()
}

RiskPlan.IsLeverageVisible = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return false;

    return ri.IsLeverageVisible()
}

RiskPlan.GetCurrencyId = function (instrument, productType)
{
    let ri = RiskPlan.GetRiskPlanItem(instrument, productType);
    if (!ri)
        return null;

    return ri.GetCurrencyId();
}

RiskPlan.GetCustomCurrencyStr = function (instrument, productType)      //#116496
{
    const currencyId = RiskPlan.GetCurrencyId(instrument, productType);
    let currencyStr = '';

    if (currencyId === null)
        return currencyStr;

    const instrumentBaseCCY = -1;
    const instrumentQuotingCCY = -2;

    if (currencyId == instrumentBaseCCY)
        currencyStr = instrument.Exp1;
    else if (currencyId == instrumentQuotingCCY)
        currencyStr = instrument.Exp2;
    else
        currencyStr = instrument.DataCache.GetAssetById(currencyId)?.Name;

    return currencyStr;
}

RiskPlan.showProductType = false;


// internal TResult GetDefaultRiskFeature<TResult>(RiskFeature feature, ProductType productType) => GetRiskFeature<TResult>(defaultRiskSettings, feature, productType);

// #region RiskPlanCache

export let RiskPlanCache = function ()
{
    this.defaultRiskSettings = new RiskPlanSettings()
    this.defaultRiskDataCache = [];
    this.riskPlanDataDictionary = {};// <key, RiskPlanChain>
    this.riskPlanDataDictionaryCount = 0;
    // this.chainByRoute = {} #85526
}

RiskPlanCache.prototype.UpdateCount = function ()
{
    this.riskPlanDataDictionaryCount = Object.keys(this.riskPlanDataDictionary).length;
}

RiskPlanCache.prototype.addRiskData = function (data)
{
    if (!data) return

    let defaultRiskSettings = this.defaultRiskSettings
    let defaultRiskDataCache = this.defaultRiskDataCache
    // let chainByRoute = this.chainByRoute #85526

    if (!data.RiskPlanDataKey.Route)
    {
        defaultRiskSettings.addItem(data.Item)
        defaultRiskDataCache.push(data)
        // Костя: сервер не гарантирует порядок настроек.
        // Добавляем дефолт дату в уже пришедшие настройки по роутам
        // for (let key in chainByRoute)  #85526
        //     chainByRoute[key].addRiskData(data)
        return
    }

    let itemKey = data.RiskPlanDataKey.toString();
    let riskPlanDataDictionary = this.riskPlanDataDictionary[itemKey] || null;
    if (!riskPlanDataDictionary)
    {
        riskPlanDataDictionary = new RiskPlanSettings();
        this.riskPlanDataDictionary[itemKey] = riskPlanDataDictionary;
    }
    riskPlanDataDictionary.addItem(data.Item);

    // let cacheItem = chainByRoute[data.RiskPlanDataKey.Route]   #85526
    // if (cacheItem)
    // {
    //     cacheItem.addRiskData(data)
    //     return
    // }

    // let cacheItem = new RiskPlanChain()   #85526
    // cacheItem.addRiskData(data)
    // chainByRoute[data.RiskPlanDataKey.Route] = cacheItem

    // Костя: сервер не гарантирует порядок настроек.
    // Добавляем уже пришедшую дефолт дату в настройки по роутам
    // for (let i = 0, len = defaultRiskDataCache.length; i < len; i++)   #85526
    //     cacheItem.addRiskData(defaultRiskDataCache[i])
}

RiskPlanCache.prototype.getDefaultRiskPlanSettings = function ()
{
    return this.defaultRiskSettings
}
RiskPlanCache.prototype.checkItem = function (cacheItem, ProductType, FieldLiteral)
{
    if (!cacheItem)
        return false

    if ((ProductType === null && ProductType === undefined) || !FieldLiteral)
        return true

    if (cacheItem.cache[ProductType] && cacheItem.cache[ProductType][FieldLiteral] !== null)
        return true

    return false
}

RiskPlanCache.prototype.getRiskPlanSettings = function (instrument, ProductType, FieldLiteral)
{
    if (!this.riskPlanDataDictionaryCount || !instrument)
        return this.defaultRiskSettings
    let cacheItem;
    //1 Всегда пробуем создать ключ для конкретного инструмента
    let riskPlanDataKey = new RiskPlanDataKey(); riskPlanDataKey.TradableId = instrument.InstrumentTradableID; riskPlanDataKey.Route = instrument.Route;
    cacheItem = this.riskPlanDataDictionary[riskPlanDataKey.toString()];
    if (this.checkItem(cacheItem, ProductType, FieldLiteral))
        return cacheItem;

    if (instrument.InstrType == InstrumentTypes.OPTIONS)
    {
        riskPlanDataKey = new RiskPlanDataKey(); riskPlanDataKey.ContractId = instrument.ContractID; riskPlanDataKey.Route = instrument.Route;
        cacheItem = this.riskPlanDataDictionary[riskPlanDataKey.toString()];
        if (this.checkItem(cacheItem, ProductType, FieldLiteral))
            return cacheItem;
    }

    if (instrument.InstrType == InstrumentTypes.OPTIONS || instrument.InstrType == InstrumentTypes.FUTURES)
    {
        riskPlanDataKey = new RiskPlanDataKey(); riskPlanDataKey.InstrumentId = instrument.Id; riskPlanDataKey.Route = instrument.Route;
        cacheItem = this.riskPlanDataDictionary[riskPlanDataKey.toString()];
        if (this.checkItem(cacheItem, ProductType, FieldLiteral))
            return cacheItem;
    }

    let resForGroups = this.GetRiskFeatureForGroup(instrument.DataCache, instrument.TypeId, instrument.Route);
    if (resForGroups)
        return resForGroups;

    riskPlanDataKey = new RiskPlanDataKey(); riskPlanDataKey.InstrumentType = instrument.InstrType; riskPlanDataKey.Route = instrument.Route;
    cacheItem = this.riskPlanDataDictionary[riskPlanDataKey.toString()];
    if (this.checkItem(cacheItem, ProductType, FieldLiteral))
        return cacheItem;

    riskPlanDataKey = new RiskPlanDataKey(); riskPlanDataKey.Route = instrument.Route;
    cacheItem = this.riskPlanDataDictionary[riskPlanDataKey.toString()];
    if (this.checkItem(cacheItem, ProductType, FieldLiteral))
        return cacheItem;

    //let route = instrument.getRoute()         #85526
    //cacheItem = this.chainByRoute[route]
    // if (!cacheItem)
    return this.defaultRiskSettings
    // return cacheItem.getRiskPlanSettings(instrument) ||
    // this.defaultRiskSettings
}


RiskPlanCache.prototype.GetRiskFeatureForGroup = function (dataCache, instrumentType, routeName)
{
    var riskPlanDataKey = new RiskPlanDataKey(); riskPlanDataKey.InstrumentType = instrumentType; riskPlanDataKey.Route = routeName;
    var cacheItem = this.riskPlanDataDictionary[riskPlanDataKey.toString()];
    if (cacheItem)
        return cacheItem;

    let instType = dataCache.getInstrumentTypeById(instrumentType);
    if (instType == null || instType.SuperTypeId == -1)
        return null;

    return this.GetRiskFeatureForGroup(dataCache, instType.SuperTypeId, routeName);
}


// #endregion RiskPlanCache

// #region RiskPlanSettings

export let RiskPlanSettings = function ()
{
    // <ProductType, RiskPlanItem>
    this.cache = {}
}

Object.defineProperty(RiskPlanSettings.prototype, 'availableProductTypes', {
    get: function () { return Object.keys(this.cache).sort() }
})

RiskPlanSettings.prototype.get = function (productType)
{
    return this.cache[productType] || null
}

RiskPlanSettings.prototype.addItem = function (item)
{
    if (!item) return null

    if (item.ProductType)
        RiskPlan.showProductType = true;

    let cache = this.cache
    if (cache[item.ProductType] && cache[item.ProductType].mergeWith)
        cache[item.ProductType].mergeWith(item)
    else
        cache[item.ProductType] = item
}

RiskPlanSettings.prototype.mergeWith = function (settings)
{
    if (!settings)
        return

    let types = this.availableProductTypes
    for (let i = 0, len = types.length; i < len; i++)
        this.addItem(settings.get(types[i]))
}

RiskPlanSettings.prototype.getCopy = function ()
{
    let copy = new RiskPlanSettings()

    let cache = this.cache
    for (let key in cache)
        copy.addItem(cache[key].getCopy())

    return copy
}

RiskPlanSettings.prototype.getValue = function (productType, literal)
{
    let c = this.cache[productType];
    if (!c || !c.getValue)
        return null;
    return c.getValue(literal);
}

// #endregion RiskPlanSettings

RiskPlan.prototype.GetUseSettledCashForStocksForOpenedPositionsInMA = function ()
{
    if (!this.riskPlanCache) return null

    let riskPlanSettings = this.riskPlanCache.getDefaultRiskPlanSettings()
    if (!riskPlanSettings) return null

    let productType = riskPlanSettings.availableProductTypes ? riskPlanSettings.availableProductTypes[0] : ProductType.General,
        riskItem = riskPlanSettings.get(productType)

    return riskItem ? riskItem.GetUseSettledCashForStocksForOpenedPositionsInMA() : null
}

RiskPlan.prototype.GetUseSettledCollateralForOpenedPositionInMA = function ()
{
    if (!this.riskPlanCache) return null

    let riskPlanSettings = this.riskPlanCache.getDefaultRiskPlanSettings()
    if (!riskPlanSettings) return null

    let productType = riskPlanSettings.availableProductTypes ? riskPlanSettings.availableProductTypes[0] : ProductType.General,
        riskItem = riskPlanSettings.get(productType)

    return riskItem ? riskItem.GetUseSettledCollateralForOpenedPositionInMA() : null
}

// #region RiskPlanChain

var RiskPlanChain = function ()
{
    let chainItem = new RiskPlanChainItemDefault(null)             // Default
    chainItem = new RiskPlanChainItemRoute(chainItem)              // Route
    //chainItem = new RiskPlanChainItemInstrumentGroup(chainItem)  // Instrument group
    //chainItem = new RiskPlanChainItemInstrument(chainItem)       // Instrument
    //chainItem = new RiskPlanChainItemSeries(chainItem)           // Series
    this.chainEntry = /*new RiskPlanChainItemStrike(*/chainItem//) // Strike

    // Костя: по цепочке будем идти снизу вверх (от страйка к дефолту).
    // Посчитал, что так логика будет немного проще,
    // т.к. нужно учитывать какие группы настроек доступны для конкретного инструмента.
    // Например: для контракта доступен только Intraday,
    // а для дефолта Intraday/Delivery. 
    // Если идти по цепочке, начиная дефолтом - 
    // получим на выходе объект с Intraday/Delivery,
    // то есть придется делать дополнительную логику
}

RiskPlanChain.prototype.addRiskData = function (data)
{
    this.chainEntry.addRiskPlanData(data)
}

RiskPlanChain.prototype.getRiskPlanSettings = function (instrument)
{
    return this.chainEntry.getRiskPlanSettings(instrument, true)
}

// #endregion RiskPlanChain

// #region RiskPlanChainItem

var RiskPlanChainItem = function (nextChainItem)
{
    this.nextChainItem = nextChainItem || null
    this.dict = {}
}

// protected abstract TKey GetKey(IInstrument instrument)
RiskPlanChainItem.prototype.getKeyFromInstrument = function (instrument) { }

// protected abstract TKey GetKey(RiskPlanData data)
RiskPlanChainItem.prototype.getKeyFromRiskPlanData = function (data) { }

RiskPlanChainItem.prototype.hasSettingsForInstrument = function (instrument)
{
    return this.dict.hasOwnProperty(
        this.getKeyFromInstrument(instrument))
}

RiskPlanChainItem.prototype.addRiskPlanData = function (data)
{
    let key = this.getKeyFromRiskPlanData(data)
    // TODO. Implement .equals().
    if (!!key) // if truthy/non-default value.
    {
        let settings = this.dict[key]
        if (!settings)
        {
            settings = new RiskPlanSettings()
            this.dict[key] = settings
        }

        settings.addItem(data.Item)
        return
    }

    if (this.nextChainItem)
        this.nextChainItem.addRiskPlanData(data)
}

RiskPlanChainItem.prototype.getRiskPlanSettings = function (instrument, firstChainItem)
{
    let nextChainItem = this.nextChainItem

    if (!this.hasSettingsForInstrument(instrument))
        return nextChainItem
            ? nextChainItem.getRiskPlanSettings(instrument, true)
            : null

    let key = this.getKeyFromInstrument(instrument)

    let settings = null

    if (nextChainItem)
    {
        settings = nextChainItem.getRiskPlanSettings(instrument, false)
        if (!settings) settings = new RiskPlanSettings()

        // При mergeWith в settings могут попасть ссылки на RiskPlanItem'ы
        // из кеша которые далее перезатираются.
        let ownSettings = this.getOwnSettings(instrument)
        if (ownSettings) ownSettings = ownSettings.getCopy()

        settings.mergeWith(ownSettings)

        // если это первое звено цепочки, нужно отфильтровать настройки по product type.
        // Например: на страйке доступен только Intraday (первое звено цепочки),
        // а на серии Intraday/Delivery, после мержа настроек, 
        // RiskPlanSettings будет хранить обе группы, Delivery нужно откинуть
        if (firstChainItem)
        {
            let result = new RiskPlanSettings()

            let availableProductTypes = ownSettings.availableProductTypes
            for (let i = 0, len = availableProductTypes.length; i < len; i++)
                result.addItem(settings.get(availableProductTypes[i]))

            settings = result
        }
    }
    else
    {
        let cacheSettings = this.dict[key]
        // создаем копию, потому что выше по цепочке будем мержить
        // с другими настройками
        settings = cacheSettings ? cacheSettings.getCopy() : new RiskPlanSettings()
    }

    return settings
}

RiskPlanChainItem.prototype.getOwnSettings = function (instrument)
{
    let key = this.getKeyFromInstrument(instrument)
    return this.dict[key] || new RiskPlanSettings()
}

// #endregion RiskPlanChainItem

// #region RiskPlanChainItemDefault

var RiskPlanChainItemDefault = function (nextChainItem)
{
    RiskPlanChainItem.call(this, nextChainItem)
    let defKey = RiskPlanChainItemDefault.DEFAULT_SETTINGS_KEY
    this.dict[defKey] = new RiskPlanSettings()
}

RiskPlanChainItemDefault.prototype = Object.create(RiskPlanChainItem.prototype)

RiskPlanChainItemDefault.DEFAULT_SETTINGS_KEY = -1

RiskPlanChainItemDefault.prototype.addRiskPlanData = function (data)
{
    let defKey = RiskPlanChainItemDefault.DEFAULT_SETTINGS_KEY

    if (!data.RiskPlanDataKey.Route)
        this.dict[defKey].addItem(data.Item)
    else
        RiskPlanChainItem.prototype.addRiskPlanData.call(this, data)
}

RiskPlanChainItemDefault.prototype.getKeyFromRiskPlanData = function (data)
{
    return RiskPlanChainItemDefault.DEFAULT_SETTINGS_KEY
}

RiskPlanChainItemDefault.prototype.getKeyFromInstrument = function (instrument)
{
    return RiskPlanChainItemDefault.DEFAULT_SETTINGS_KEY
}

// #endregion RiskPlanChainItemDefault

// #region RiskPlanChainItemRoute

var RiskPlanChainItemRoute = function (nextChainItem)
{
    RiskPlanChainItem.call(this, nextChainItem)
}

RiskPlanChainItemRoute.prototype = Object.create(RiskPlanChainItem.prototype)

RiskPlanChainItemRoute.prototype.getKeyFromRiskPlanData = function (data)
{
    return data.RiskPlanDataKey.Route || null
}

RiskPlanChainItemRoute.prototype.getKeyFromInstrument = function (instrument)
{
    return instrument.getRoute()
}


// #endregion RiskPlanChainItemRoute